import React, { useState, useEffect, useMemo } from 'react';
import { Dropdown, DropdownProps, List } from 'tea-component';
import classNames from 'classnames';
import ConfirmAndCancel from '@/pages/datalab/components/ConfirmAndCancle';
import './index.scss';

/**
 * 选项联合类型（前向声明，用于 selected 函数签名）
 * @template T - 选项值的类型
 */
type TSelectorOption<T = unknown> = ITextOption<T> | ICustomRenderOption<T>;

/**
 * 文本选项接口
 * @template T - 选项值的类型
 */
interface ITextOption<T = unknown> {
  /** 选项类型标识 */
  type: 'text';
  /** 选项显示文本 */
  text: React.ReactNode;
  /** 选项的值，不能是函数类型 */
  value: T;
  /** 可选的选中判断函数 */
  selected?: (option: TSelectorOption<T>, internalValue: T | undefined) => boolean;
  /** 可选的禁用状态 */
  disabled?: boolean;
}

/**
 * 自定义渲染选项接口
 * @template T - 选项值的类型
 */
interface ICustomRenderOption<T = unknown> {
  /** 选项类型标识 */
  type: 'component';
  /** 
   * 选项显示的 React 组件渲染函数
   * @param value - 组件选项的内部状态值（弹窗内部维护）
   * @param onChange - 更新组件选项内部状态的方法
   */
  render: (value: T, onChange: (newValue: T) => void) => React.ReactNode;
  /** 可选的选中判断函数 */
  selected?: (option: TSelectorOption<T>, internalValue: T | undefined) => boolean;
  /** 可选的禁用状态 */
  disabled?: boolean;
}

/**
 * 组件 Props 接口（使用泛型优化 value 类型）
 * @template T - 选项值的类型
 */
interface ISelectorWithConfirmProps<T = unknown>
  extends Omit<DropdownProps, 'button' | 'children'> {
  /** 受控组件的值，对应选中项的最终值（确认后的值） */
  value?: T;
  /** 值变化时的回调，参数是确认后的最终值 */
  onChange?: (value: T) => void;
  /** 选项列表 */
  options: TSelectorOption<T>[];
  /** 当受控 value 没有值的时候显示的占位符 */
  placeholder?: string;
  /** 可选的头部组件（如 SelectWithButton） */
  headerComponent?: React.ReactNode;
  /** Dropdown 的 button 显示内容，默认根据选中项和 buttonText 计算 */
  button?: React.ReactNode | ((controlledValue?: T, selectedOption?: TSelectorOption<T>) => React.ReactNode);
  /** 确认按钮的回调，默认会关闭弹窗并触发 onChange */
  onConfirm?: (value: T) => void;
  /** 取消按钮的回调，默认会关闭弹窗并恢复状态 */
  onCancel?: () => void;
}

/**
 * Selector With Confirm 组件
 * 支持文本选项和组件选项的单选下拉选择器，带确认/取消按钮
 * @template T - 选项值的类型
 * @param props - 组件属性
 * @returns 返回 SelectorWithConfirm 组件实例
 */
const SelectorWithConfirm = <T = unknown,>(props: ISelectorWithConfirmProps<T>) => {
  const {
    value: controlledValue,
    onChange,
    options,
    placeholder = '请选择',
    headerComponent,
    button,
    onConfirm,
    onCancel,
    overlayClassName,
    ...rest
  } = props;
  
  /**
   * 判断是否为文本类型选项
   * @param option - 待判断的选项
   * @returns 如果是文本选项返回 true，否则返回 false
   */
  const isTextOption = (option: TSelectorOption<T>): option is ITextOption<T> => {
    return option?.type === 'text';
  };

  // 弹窗内部临时选中状态
  const [internalValue, setInternalValue] = useState<T | undefined>(controlledValue);

  // 当受控 value 变化时，同步更新内部状态
  useEffect(() => {
    setInternalValue(controlledValue);
  }, [controlledValue]);

  /**
   * 打开弹窗时的初始化
   * 初始化内部状态为受控 value，确保每次打开弹窗时状态一致
   */
  const handleOpen = (): void => {
    setInternalValue(controlledValue);
  };

  /**
   * 判断选项是否被选中
   * @param option - 待判断的选项
   * @param value - 当前的值
   * @returns 如果选项被选中返回 true，否则返回 false
   */
  const isOptionSelected = (option: TSelectorOption<T>, value: T | undefined): boolean => {
    if (value === undefined || value === null) {
      return false;
    }
    if (option.selected) {
      return option.selected(option, value);
    }

    // 默认判断逻辑：通过值比较（仅文本选项有 value）
    if (isTextOption(option)) {
      return option.value === value;
    }
    // 自定义渲染选项没有 value，只能通过 selected 函数判断
    return false;
  };

  /**
   * 处理列表点击事件（事件委托，提升性能）
   * @param event - 点击事件
   */
  const handleListClick = (event: React.MouseEvent<HTMLDivElement>): void => {
    const target = event.target as HTMLElement;
    // 查找最近的 List.Item 元素
    const listItem = target.closest('.dbl-selector-with-confirm-text-option');
    if (!listItem) {
      return;
    }

    // 获取 data-index 属性
    const indexStr = listItem.getAttribute('data-index');
    if (indexStr === null) {
      return;
    }

    const index = parseInt(indexStr, 10);
    if (isNaN(index) || index < 0 || index >= options.length) {
      return;
    }

    const option = options[index];
    if (option.disabled) {
      return;
    }
    
    if (isTextOption(option)) {
      // 文本选项：直接设置 option.value
      setInternalValue(option?.value);
    }
    // 组件选项不配置value, 点击时也不受内部控制.
  };

  /**
   * 处理确认按钮点击事件
   * 触发 onChange 和 onConfirm 回调，然后关闭弹窗
   * @param close - 关闭弹窗的函数
   */
  const handleConfirm = (close: () => void): void => {
    if (internalValue !== undefined && internalValue !== null) {
      onChange?.(internalValue);
      onConfirm?.(internalValue);
    }
    close();
  };

  /**
   * 处理取消按钮点击事件
   * 恢复内部状态到打开弹窗前的值，然后关闭弹窗
   * @param close - 关闭弹窗的函数
   */
  const handleCancel = (close: () => void): void => {
    // 恢复内部状态到打开弹窗前的值
    setInternalValue(controlledValue);
    onCancel?.();
    close();
  };
  
  /**
   * 渲染 button 内容（使用 useMemo 缓存，避免每次渲染都执行）
   * 根据 button 属性的类型（函数、ReactNode 或默认）返回相应的内容
   * @returns 返回要显示在按钮上的内容
   */
  const buttonContent = useMemo((): React.ReactNode => {
    if (button && typeof button !== 'function') {
      return button;
    }
    
    const selectedOption = options.find((opt) => {
      if (opt.selected) {
        return opt.selected(opt, controlledValue);
      }
      // 默认判断逻辑：通过值比较（仅文本选项有 value）
      if (isTextOption(opt)) {
        return opt.value === controlledValue;
      }
      // 自定义渲染选项没有 value，只能通过 selected 函数判断
      return false;
    });
    
    if (typeof button === 'function') {
      return button(controlledValue, selectedOption);
    }
    
    if (selectedOption && isTextOption(selectedOption)) {
      return selectedOption.text ?? placeholder ?? '';
    }
    return placeholder ?? '';
  }, [button, controlledValue]);

  return (
    <Dropdown
      button={buttonContent}
      appearance="button"
      matchButtonWidth
      clickClose={false}
      onOpen={handleOpen}
      overlayClassName={classNames('dbl-selector-with-confirm-overlay', overlayClassName)}
      {...rest}
    >
      {(close) => (
        <div className="dbl-selector-with-confirm">
          {headerComponent && (
            <div className="dbl-selector-header">
              {headerComponent}
            </div>
          )}
          <div onClick={handleListClick}>
            <List type="option" className="dbl-selector-list">
              {options.map((option, index) => {
                const isSelected = isOptionSelected(option, internalValue);
                const isDisabled = option.disabled;

                if (isTextOption(option)) {
                  return (
                    <List.Item
                      className={'dbl-selector-with-confirm-text-option'}
                      key={`text-${index}-${option.value}`}
                      data-index={index}
                      selected={isSelected}
                      disabled={isDisabled}
                    >
                      {option.text}
                    </List.Item>
                  );
                }

                return (
                  <List.Item
                    key={`component-${index}`}
                    data-index={index}
                    selected={isSelected}
                    disabled={isDisabled}
                  >
                    {option.render(
                      internalValue as T,
                      setInternalValue
                    )}
                  </List.Item>
                );
              })}
            </List>
          </div>
          <ConfirmAndCancel
            className="dbl-selector-with-confirm-btn"
            onConfirm={() => handleConfirm(close)}
            onCancel={() => handleCancel(close)}
          />
        </div>
      )}
    </Dropdown>
  );
};

export default SelectorWithConfirm;
export type {
  ISelectorWithConfirmProps,
  TSelectorOption,
  ITextOption,
  ICustomRenderOption,
};

